import { fetchAuthSessionWithLoginMismatchRetry } from "../../utils/amplifyAuthRecovery";
import { eventLivePageBase } from "../globalUtils";
import { evtLiveOwnerHeaders } from "../teams/workspace";

// evt.live routes that act on the signed-in user's Stripe money. They need the
// user's access token, so they can't be plain links any more:
// - connect-link: a signed link to connect the Stripe account that receives an
//   event's ticket payouts (only the event's owner gets one);
// - billing-portal: Stripe's billing portal for the user's own subscription;
// - payout-accounts: the Stripe accounts the user's events already pay out
//   to, which Sell Tickets offers for an event without one (eventlive-domain
//   docs/stripe-connect.md, "Using a Stripe account from another event").
// Callers of the first two fall back to the old links, which still work but go
// through email: the owner confirms the payout account, the billing link is
// emailed. In someone else's account (Teams) connect-link and payout-accounts
// name the owner (x-el-owner), and evt.live lets the account's admins use them;
// the billing portal is always your own.

const REQUEST_TIMEOUT_MS = 15000;

const getAccessToken = async () => {
  try {
    const session = await fetchAuthSessionWithLoginMismatchRetry();
    return session?.tokens?.accessToken?.toString() || null;
  } catch (error) {
    return null;
  }
};

// The answer's JSON. Errors carry evt.live's message (shown to the user) and
// the HTTP status.
async function requestWithToken(method, url, body, extraHeaders = {}) {
  const token = await getAccessToken();
  if (!token) throw new Error("Not signed in");
  const controller =
    typeof AbortController !== "undefined" ? new AbortController() : null;
  const timeout = controller
    ? setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS)
    : null;
  try {
    const response = await fetch(url, {
      method,
      headers: {
        Authorization: `Bearer ${token}`,
        ...(body ? { "Content-Type": "application/json" } : {}),
        ...extraHeaders,
      },
      ...(body ? { body: JSON.stringify(body) } : {}),
      ...(controller ? { signal: controller.signal } : {}),
    });
    const data = await response.json().catch(() => null);
    if (!response.ok || !data) {
      throw Object.assign(new Error(data?.error || `HTTP ${response.status}`), {
        status: response.status,
      });
    }
    return data;
  } finally {
    if (timeout) clearTimeout(timeout);
  }
}

async function postWithToken(path, body, extraHeaders) {
  const data = await requestWithToken(
    "POST",
    `${eventLivePageBase}${path}`,
    body,
    extraHeaders
  );
  if (typeof data.url !== "string") throw new Error("No link in the answer");
  return data.url;
}

// The old, unsigned link: still accepted, but the payout account then waits
// for the owner to confirm it by email.
export const legacyStripeConnectLink = (event) =>
  `${eventLivePageBase}api/stripe/payperviewredirect?broadcaster=${event.broadcaster}&slug=${event.slug}`;

// purpose: "owner" (Connect Stripe Account) or "client" (Copy Link…).
export const fetchStripeConnectLink = (slug, purpose) =>
  postWithToken(
    "api/stripe/connect-link",
    { slug, purpose },
    evtLiveOwnerHeaders(slug)
  );

// env: "prod" or "test", as the old portal link used.
export const fetchBillingPortalUrl = (env) =>
  postWithToken("api/stripe/billing-portal", { env });

// The old portal link. It now emails the customer a link to the portal.
export const legacyBillingPortalLink = (env, stripeCustomerId) =>
  `${eventLivePageBase}api/openStripePortal/${env}/${stripeCustomerId}`;

// A Netlify preview of a branch can send these to evt.live's preview of the
// same change (EXPO_PUBLIC_PAYOUT_API_BASE, set per branch in netlify.toml);
// every other build uses evt.live.
const PAYOUT_ACCOUNTS = `${(
  process.env.EXPO_PUBLIC_PAYOUT_API_BASE || eventLivePageBase
).replace(/\/?$/, "/")}api/stripe/payout-accounts`;

// {business, accounts: [{account, label, kind, eventCount, events}]}
export const fetchPayoutAccounts = () =>
  requestWithToken("GET", PAYOUT_ACCOUNTS, undefined, evtLiveOwnerHeaders());

// That account for that event: {account, label, changed}.
export const choosePayoutAccount = (account, slug) =>
  requestWithToken(
    "POST",
    PAYOUT_ACCOUNTS,
    { action: "use", account, slug },
    evtLiveOwnerHeaders(slug)
  );
